import { trpc } from '../../../../utils/trpc';
import { SelfServeSCCloudTrackerSteps } from './Configs/SelfServeSCCloudTrackerSteps';
import useUser from '../../../../lib/useUser';
import { useContext, useEffect, useState } from 'react';
import { EmrNotSelectedComponent } from '../EMRNotSelected';
import { DigitalFulfillmentModal } from '../../../../modals/DigitalFulfillmentModal';
import { useAutofulfillNudgeThreshold } from '../../../ProviderTools/utils/dfUtils';
import { SelfServeSCCloudNoEncountersTrackerSteps } from './Configs/SelfServeSCCloudNoEncountersTrackerSteps';
import { SelfServeSCOnPremTrackerSteps } from './Configs/SelfServeSCOnPremTrackerSteps';
import { AgreementModal } from '../AgreementModal';
import { AddressConfirmationModal } from '../AddressConfirmation/AddressConfirmationModal';
import { EncounterFilterModal } from '../EncounterFilterModal';
import { NextStepsWrapper } from '../nextStepModals/NextStepsWrapper';
import { useSteps } from '@chakra-ui/react';
import { NextRouter, useRouter } from 'next/router';
import { UserWithPermissions } from '../../../../lib/user';
import { DisplayStep, StepDisplay } from '../AutofulfillStepper';
import { LightWeightTrackerSteps } from './Configs/LightWeightTrackerSteps';
import { DigitalOnboardingStatus, TenantWithMetadata } from '../../../../generated/tenant-api';
import { UiKit } from '@datavant/uikit';
import { AgreementModalContext } from '../../../../lib/contexts/AgreementModalContext';
import { ScannedDocumentsModal } from '../ScannedDocumentsModal';
import { SelfServeSCECWCloudTrackerSteps } from './Configs/SelfServeSCECWCloudTrackerSteps';

export const RM_SITECONNECT_EMR_MAP: Record<string, string> = {
    'eClinicalWorks': 'eCW Cloud',
    'Modernizing Medicine': 'Modernizing Medicine',
    'NextGen Office': 'NextGen Office',
    'Simple Practice': 'Simple Practice',
};

export type EMRConfigData = {
    emr: string;
    onboardingStatus: DigitalOnboardingStatus;
    addressFeedbackComplete?: boolean | null;
    digitalSiteId?: number | null;
};

export type ModalControls = {
    setIsDfModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
    setIsAddressConfirmationModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
    setIsNextStepsModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
    setIsEncounterFilterModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
    setIsAgreementModalOpen: (isOpen: boolean) => void;
    setIsScannedDocConfirmationModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
};

const getTrackerSteps = (
    isSideNavOpen: boolean,
    tenantData: TenantWithMetadata | null | undefined,
    currentUser: UserWithPermissions | undefined,
    router: NextRouter,
    modalControls: ModalControls
) => {
    if (!(tenantData?.digitalMetadata?.emr && tenantData?.digitalMetadata?.onboardingStatus && currentUser)) {
        return [];
    }

    let steps: DisplayStep[] = [];

    const firstTitle = (
        <UiKit.Chakra.VStack
            align="start"
            width={'100%'}
            paddingTop={
                tenantData.digitalMetadata.onboardingStatus === DigitalOnboardingStatus.EMR_SUBMITTED
                    ? '20px'
                    : '0px'
            }
        >
            <span
                title={tenantData.digitalMetadata.emr}
                style={{
                    textOverflow: 'ellipsis',
                    width: '150px',
                    overflow: 'hidden',
                    whiteSpace: 'initial',
                    display: '-webkit-box',
                    WebkitBoxOrient: 'vertical',
                    WebkitLineClamp: 2,
                }}
            >
                {tenantData.digitalMetadata.emr}
            </span>
            {tenantData.digitalMetadata.onboardingStatus === DigitalOnboardingStatus.EMR_SUBMITTED && (
                <UiKit.Chakra.Box paddingBottom={'40px'}>
                    <UiKit.Tag.Success showIcon isSmall text="Eligible" />
                </UiKit.Chakra.Box>
            )}
        </UiKit.Chakra.VStack>
    );

    const configData: EMRConfigData = {
        emr: tenantData.digitalMetadata.emr,
        onboardingStatus: tenantData.digitalMetadata.onboardingStatus,
        addressFeedbackComplete: tenantData.digitalMetadata.addressFeedbackComplete,
        digitalSiteId: tenantData.digitalMetadata.digitalSiteId,
    };

    switch (tenantData.digitalMetadata.emrConfigId) {
        case 'SELF_SERVE_SITECONNECT_CLOUD':
            steps = SelfServeSCCloudTrackerSteps({
                isSideNavOpen,
                configData,
                currentUser: currentUser,
                router: router,
                modalControls,
            });
            break;
        case 'SELF_SERVE_SITECONNECT_ECW_CLOUD':
            steps = SelfServeSCECWCloudTrackerSteps({
                isSideNavOpen,
                configData,
                currentUser: currentUser,
                router: router,
                modalControls,
            });
            break;
        case 'SELF_SERVE_SITECONNECT_CLOUD_NO_ENCOUNTERS':
            steps = SelfServeSCCloudNoEncountersTrackerSteps({
                isSideNavOpen,
                configData,
                currentUser: currentUser,
                router: router,
                modalControls,
            });
            break;
        case 'SELF_SERVE_SITECONNECT_ON_PREM':
            steps = SelfServeSCOnPremTrackerSteps({
                isSideNavOpen,
                configData,
                currentUser: currentUser,
                router: router,
                modalControls,
            });
            break;
        case 'LIGHTWEIGHT':
            steps = LightWeightTrackerSteps({
                isSideNavOpen,
                configData,
                currentUser: currentUser,
                router: router,
                modalControls,
            });
            break;
    }

    if (steps.length !== 0) {
        steps = [{ title: firstTitle }, ...steps];
    }

    return steps;
};

export const RRAOnboardingTracker = (props: { isSideNavOpen: boolean }) => {
    const currentUser = useUser().user;
    const router = useRouter();
    const agreementModalContext = useContext(AgreementModalContext);

    const { data, isLoading } = trpc.tenant.getTenant.useQuery(undefined, {
        staleTime: Infinity, // ✅ Data is always considered fresh
        cacheTime: Infinity, // ✅ Cache never garbage-collected
        refetchOnMount: false, // 🚫 Don't re-fetch on component remount
        refetchOnWindowFocus: false, // 🚫 Don't re-fetch when switching tabs
        refetchOnReconnect: false, // 🚫 Don't re-fetch when network reconnects
    });
    const emr = data?.digitalMetadata?.emr;
    const onboardingStatus = data?.digitalMetadata?.onboardingStatus;
    const showNudge = useAutofulfillNudgeThreshold();

    const [isDfModalOpen, setIsDfModalOpen] = useState<boolean>(false);
    const [isAddressConfirmationModalOpen, setIsAddressConfirmationModalOpen] = useState(false);
    const [isNextStepsModalOpen, setIsNextStepsModalOpen] = useState(false);
    const [isEncounterFilterModalOpen, setIsEncounterFilterModalOpen] = useState(false);
    const [isScannedDocConfirmationModalOpen, setIsScannedDocConfirmationModalOpen] = useState(false);
    const modalControls: ModalControls = {
        setIsDfModalOpen,
        setIsAddressConfirmationModalOpen,
        setIsScannedDocConfirmationModalOpen,
        setIsNextStepsModalOpen,
        setIsEncounterFilterModalOpen,
        setIsAgreementModalOpen: agreementModalContext.setIsOpen,
    };

    const trackerSteps = getTrackerSteps(props.isSideNavOpen, data, currentUser, router, modalControls);

    const { activeStep, setActiveStep } = useSteps({
        index: 0,
        count: trackerSteps.length,
    });

    useEffect(() => {
        onboardingStatus &&
            setActiveStep(
                trackerSteps.findIndex(trackerSteps => trackerSteps.statuses?.includes(onboardingStatus))
            );
    }, [onboardingStatus, trackerSteps]);

    const hasProgressedBeyondDocusignCompleted =
        emr &&
        onboardingStatus &&
        onboardingStatus !== DigitalOnboardingStatus.EMR_SUBMITTED &&
        onboardingStatus !== DigitalOnboardingStatus.DOCUSIGN_COMPLETED;

    if (
        currentUser?.isDatavanter ||
        data?.digitalMetadata?.isLiveDigitalFulfillment ||
        isLoading ||
        (data?.digitalMetadata?.emr && data?.digitalMetadata?.eligibility === false) ||
        (!showNudge && !hasProgressedBeyondDocusignCompleted)
    ) {
        return <></>;
    }

    return (
        <>
            {!emr ? (
                <EmrNotSelectedComponent
                    isSideNavOpen={props.isSideNavOpen}
                    setIsDfModalOpen={setIsDfModalOpen}
                />
            ) : (
                <StepDisplay
                    title="AutoFulfill Setup Tracker"
                    activeStep={activeStep}
                    steps={trackerSteps}
                    isSideNavOpen={props.isSideNavOpen}
                />
            )}
            <DigitalFulfillmentModal isOpen={isDfModalOpen} setIsOpen={setIsDfModalOpen} />
            {agreementModalContext.isOpen && ( // so docusign api stuff doesnt load until ready
                <AgreementModal
                    onClose={() => agreementModalContext.setIsOpen(false)}
                    setIsNextStepsModalOpen={setIsNextStepsModalOpen}
                />
            )}
            <NextStepsWrapper
                onClose={() => setIsNextStepsModalOpen(false)}
                isOpen={isNextStepsModalOpen}
                eligibleForSelfServeTracker={data?.digitalMetadata?.selfServeEligibility ?? false}
                emr={emr}
                emrEnvironment={data?.digitalMetadata?.emrEnvironment}
                fulfillmentMethod={data?.digitalMetadata?.fulfillmentMethod}
            />
            <AddressConfirmationModal
                isOpen={isAddressConfirmationModalOpen}
                onClose={() => setIsAddressConfirmationModalOpen(false)}
            />
            <ScannedDocumentsModal
                isOpen={isScannedDocConfirmationModalOpen}
                onClose={() => setIsScannedDocConfirmationModalOpen(false)}
            />
            <EncounterFilterModal
                isOpen={isEncounterFilterModalOpen}
                onClose={() => setIsEncounterFilterModalOpen(false)}
            />
        </>
    );
};
